import React from 'react';

/**
 * The determinate progress bar, in one place.
 *
 * Four screens had drawn their own copy of the same markup — a `bg-slate-200` track with a
 * `bg-primary` fill — and the copies had drifted: the crawl banners are a 1px hairline across the
 * top of a page, while the export modal needs a bar someone watches. A 1px hairline on a white
 * modal reads as nothing at all until it is most of the way across, which is what made the export
 * look like it had no track.
 *
 * `hairline` is the page-top banner; `bar` is the one you watch, with a track dark enough to show
 * where the fill has not reached yet, and its own percentage beside the label so the number is
 * readable even at a few percent.
 */
export interface ProgressBarProps {
  /** 0–100. Values outside the range are clamped; a non-finite one reads as 0. */
  percent: number;
  /** Shown under the bar. The percentage is appended to it in the `bar` size. */
  label?: React.ReactNode;
  /** Tooltip and accessible name. Falls back to the label when that is plain text. */
  title?: string;
  size?: 'hairline' | 'bar';
  className?: string;
}

function clampPercent(percent: number): number {
  if (typeof percent !== 'number' || !Number.isFinite(percent)) {
    return 0;
  }

  return Math.max(0, Math.min(100, Math.round(percent)));
}

export function ProgressBar({ percent, label, title, size = 'bar', className }: ProgressBarProps) {
  const value = clampPercent(percent);
  const accessibleName = title ?? (typeof label === 'string' ? label : 'Progress');
  const hairline = size === 'hairline';

  return (
    <div className={className}>
      <div
        className={hairline ? 'w-full h-1 bg-slate-200' : 'w-full h-2.5 overflow-hidden rounded-full border border-slate-300 bg-slate-200'}
        title={title}
        role="progressbar"
        aria-label={accessibleName}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full bg-primary transition-[width] duration-500 ${hairline ? '' : 'rounded-full'}`}
          style={{ width: `${value}%` }}
        />
      </div>
      {label ? (
        <div className={hairline ? 'text-muted mt-2' : 'mt-2 text-sm text-slate-600'}>
          {label}
          {hairline ? null : ` — ${value}%`}
        </div>
      ) : null}
    </div>
  );
}
